import type { Easing } from "./types.ts";

const C1 = 1.70158;
const C3 = C1 + 1;
const C4 = (2 * Math.PI) / 3;


/** Penner-style easings, written once as "in" curves or "out" curves on u in (0,1). */
const IN: Record<string, (u: number) => number> = {
  sine: (u) => 1 - Math.cos((u * Math.PI) / 2),
  quad: (u) => u * u,
  cubic: (u) => u * u * u,
  quart: (u) => u ** 4,
  quint: (u) => u ** 5,
  expo: (u) => 2 ** (10 * u - 10),
  circ: (u) => 1 - Math.sqrt(1 - u * u),
  back: (u) => C3 * u * u * u - C1 * u * u,
  elastic: (u) => -(2 ** (10 * u - 10)) * Math.sin((u * 10 - 10.75) * C4),
  bounce: (u) => 1 - bounceOut(1 - u),
};

function bounceOut(u: number): number {
  const n1 = 7.5625, d1 = 2.75;
  if (u < 1 / d1) return n1 * u * u;
  if (u < 2 / d1) { const v = u - 1.5 / d1; return n1 * v * v + 0.75; }
  if (u < 2.5 / d1) { const v = u - 2.25 / d1; return n1 * v * v + 0.9375; }
  const v = u - 2.625 / d1; return n1 * v * v + 0.984375;
}

/** "out" is the mirror of "in": 1 - in(1 - u). "in_out" glues the two halves. */
function family(name: string, mode: "in" | "out" | "in_out", u: number): number {
  const f = IN[name];
  if (!f) return u;
  if (mode === "in") return f(u);
  if (mode === "out") return 1 - f(1 - u);
  return u < 0.5 ? f(2 * u) / 2 : 1 - f(2 - 2 * u) / 2;
}

type Fn = (u: number) => number;
const parsed = new Map<string, Fn>();

/** Solve the x(t) of a CSS-style cubic bezier by Newton + bisection, return y. */
function cubicBezier(x1: number, y1: number, x2: number, y2: number): Fn {
  const cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx;
  const cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
  const X = (t: number) => ((ax * t + bx) * t + cx) * t;
  const Y = (t: number) => ((ay * t + by) * t + cy) * t;
  const dX = (t: number) => (3 * ax * t + 2 * bx) * t + cx;
  return (u) => {
    let t = u;
    for (let i = 0; i < 6; i++) {
      const e = X(t) - u;
      if (Math.abs(e) < 1e-6) return Y(t);
      const d = dX(t);
      if (Math.abs(d) < 1e-6) break;
      t -= e / d;
    }
    let lo = 0, hi = 1;
    t = u;
    for (let i = 0; i < 24; i++) {
      const e = X(t);
      if (Math.abs(e - u) < 1e-6) break;
      if (e < u) lo = t; else hi = t;
      t = (lo + hi) / 2;
    }
    return Y(t);
  };
}

/**
 * Damped spring settling on 1. `bounce` in [0,1): 0 = critically damped (no overshoot), towards 1 = very springy.
 * The oscillation is normalised so that f(1) = 1 exactly: the envelope is forced to zero at u = 1.
 */
function spring(bounce: number): Fn {
  const b = Math.min(Math.max(bounce, 0), 0.95);
  if (b === 0) return (u) => 1 - (1 + 8 * u) * Math.exp(-8 * u) + (1 + 8) * Math.exp(-8) * u;
  const zeta = 1 - b * 0.92;                        // damping ratio
  const w = 2 * Math.PI * (1.5 + 3 * b);            // angular frequency
  const decay = 6 / Math.max(zeta, 0.08) / 2;
  const raw = (u: number) => 1 - Math.exp(-decay * u * zeta * 2) * Math.cos(w * u);
  const end = raw(1);
  return (u) => raw(u) - (end - 1) * u;
}

function nums(s: string): number[] { return s.split(",").map((x) => parseFloat(x.trim())); }

function parse(kind: string): Fn | null {
  const m = /^([a-z_]+)\(([^)]*)\)$/.exec(kind);
  if (m) {
    const a = nums(m[2]!);
    if (a.some((x) => !Number.isFinite(x))) return null;
    if (m[1] === "cubic_bezier" && a.length === 4) {
      const [x1, y1, x2, y2] = a as [number, number, number, number];
      return cubicBezier(Math.min(Math.max(x1, 0), 1), y1, Math.min(Math.max(x2, 0), 1), y2);
    }
    if (m[1] === "spring") return spring(a[0] ?? 0.4);
    if (m[1] === "steps") {
      const n = Math.max(1, Math.min(64, Math.round(a[0] ?? 1)));
      return (u) => Math.min(Math.floor(u * n) / n, 1);
    }
    return null;
  }
  const f = /^(in|out|in_out)_([a-z]+)$/.exec(kind);
  if (f && IN[f[2]!]) { const mode = f[1] as "in" | "out" | "in_out", name = f[2]!; return (u) => family(name, mode, u); }
  return null;
}

/** Easing curves on u in [0,1]. Every curve satisfies f(0)=0 and f(1)=1 exactly. `hold` is a step. */
export function ease(kind: Easing, u: number): number {
  if (!(u > 0)) return 0;          // also handles NaN
  if (u >= 1) return 1;
  switch (kind) {
    case "linear": return u;
    case "ease_in": return u * u * u;
    case "ease_out": return 1 - (1 - u) ** 3;
    case "ease_in_out": return u < 0.5 ? 4 * u * u * u : 1 - ((-2 * u + 2) ** 3) / 2;
    case "ease_out_back": return 1 + C3 * (u - 1) ** 3 + C1 * (u - 1) ** 2;   // overshoots ~10%
    case "ease_out_expo": return 1 - 2 ** (-10 * u);
    case "hold": return 0;
  }
  let fn = parsed.get(kind);
  if (!fn) {
    fn = parse(kind) ?? ((x: number) => x);
    if (parsed.size > 512) parsed.clear();
    parsed.set(kind, fn);
  }
  return fn(u);
}

